import { go } from 'fxjs/es';
import {
  createCanvasElement,
  destinationInCanvas,
  makeCloneCanvas,
  makeGradientCanvas,
  rgbToHex,
} from '../../../../../../Canvas/S/util.js';
import { shadow } from './core.js';

function renderTexture(dest_c, hex, material_c) {
  const ctx = dest_c.getContext('2d');
  if (hex) {
    ctx.fillStyle = hex;
    ctx.fillRect(0, 0, dest_c.width, dest_c.height);
  } else {
    ctx.drawImage(material_c, 0, 0, material_c.width, material_c.height, 0, 0, dest_c.width, dest_c.height);
  }
  return dest_c;
}

function makeCanvasTextured(src_c, hex, material_c) {
  const { width, height } = src_c;
  return go(
    createCanvasElement({ width, height }),
    (c) => renderTexture(c, hex, material_c),
    (c) => destinationInCanvas(c, src_c),
  );
}

function white(src_c, ratio) {
  const step = 2 * ratio;
  const brightness = 255;
  const hex = rgbToHex(brightness, brightness, brightness);
  const layer1 = makeCanvasTextured(src_c, hex);
  return go(layer1, (c) => {
    const ctx = c.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    const x = step;
    const y = step;
    ctx.drawImage(c, 0, 0, c.width, c.height, x || 0, y || 0, c.width, c.height);
    return c;
  });
}

function masking({ mask, target }) {
  const canvas = makeCloneCanvas(target);
  const ctx = canvas.getContext('2d');
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(mask, 0, 0);
  return canvas;
}

function makeMarginCanvas(canvas, margin_x, margin_y) {
  const c = document.createElement('canvas');
  c.width = canvas.width + margin_x;
  c.height = canvas.height + margin_y;
  const ctx = c.getContext('2d');
  ctx.drawImage(canvas, 0, margin_y);
  return c;
}

export async function makeShadowAndGeometryCanvas(
  src_c,
  {
    right_solid_opacity,
    right_solid_top_hex,
    right_solid_bottom_hex,
    right_solid_center_hex,
    right_solid_x,
    right_solid_y,
  },
  {
    left_solid_opacity,
    left_solid_top_hex,
    left_solid_bottom_hex,
    left_solid_center_hex,
    left_solid_x,
    left_solid_y,
  },
  shadow_brightness,
  ratio,
) {
  const c = createCanvasElement(src_c);
  const ctx = c.getContext('2d');
  go(
    makeGradientCanvas(
      c,
      [
        {
          offset: 0,
          hex: right_solid_top_hex,
        },
        {
          offset: 0.4,
          hex: right_solid_top_hex,
        },
        {
          offset: 0.5,
          hex: right_solid_center_hex,
        },
        {
          offset: 0.75,
          hex: right_solid_bottom_hex,
        },
        {
          offset: 1,
          hex: right_solid_bottom_hex,
        },
      ],
      { x1: c.width / 2, y1: 0, x2: c.width / 2, y2: c.height },
    ),
    (c) => masking({ target: c, mask: src_c }),
    (gradient_layer) => {
      const c = createCanvasElement(gradient_layer);
      const ctx = c.getContext('2d');
      ctx.drawImage(
        gradient_layer,
        0,
        0,
        gradient_layer.width,
        gradient_layer.height,
        right_solid_x * ratio,
        right_solid_y * ratio,
        gradient_layer.width,
        gradient_layer.height,
      );
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(
        gradient_layer,
        0,
        0,
        gradient_layer.width,
        gradient_layer.height,
        0,
        0,
        gradient_layer.width,
        gradient_layer.height,
      );
      return c;
    },
    (c) => makeOpacity(c, right_solid_opacity),
    (c) => ctx.drawImage(c, 0, 0),
  );

  const shadow_c = await shadow(src_c, shadow_brightness, ratio);
  ctx.drawImage(shadow_c, 0, 0);

  return c;
}
function makeOpacity(c, opacity) {
  const canvas = createCanvasElement(c);
  const ctx = canvas.getContext('2d');
  ctx.globalAlpha = opacity;
  ctx.drawImage(c, 0, 0);
  return canvas;
}

export async function acrylicStandEffect({ outline_canvas, design_canvas }) {
  const margin_x = 0;
  const margin_y = 4;
  outline_canvas = makeMarginCanvas(outline_canvas, margin_x, margin_y);
  design_canvas = makeMarginCanvas(design_canvas, margin_x, margin_y);
  const c = createCanvasElement({
    width: outline_canvas.width,
    height: outline_canvas.height,
  });
  const ctx = c.getContext('2d');
  ctx.drawImage(design_canvas, 0, 0);
  ctx.drawImage(
    await makeShadowAndGeometryCanvas(
      makeCloneCanvas(outline_canvas),
      {
        right_solid_opacity: 0.5,
        right_solid_top_hex: '#c1c1c1',
        right_solid_center_hex: '#696969',
        right_solid_bottom_hex: '#c1c1c1',
        right_solid_x: margin_x,
        right_solid_y: -margin_y,
      },
      {},
      225,
      1,
    ),
    0,
    0,
  );
  ctx.drawImage(
    await go(
      makeGradientCanvas(
        c,
        [
          {
            offset: 0,
            hex: 'rgba(200, 200, 200, 0)',
          },
          {
            offset: 0.4,
            hex: 'rgba(200, 200, 200, 0.2)',
          },
          {
            offset: 0.5,
            hex: 'rgba(255, 255, 255, 0.2)',
          },
          {
            offset: 0.6,
            hex: 'rgba(200, 200, 200, 0.2)',
          },
          {
            offset: 1,
            hex: 'rgba(200, 200, 200, 0)',
          },
        ],
        { x1: 0, y1: 0, x2: c.width, y2: c.height },
      ),
      async (c) => masking({ mask: outline_canvas, target: c }),
    ),
    0,
    0,
  );
  const white_c = white(outline_canvas, 1);
  ctx.drawImage(white_c, 0, 0);
  return c;
  // const { top, left, width, height } = trimCanvasSize(c);
  // const image_data = ctx.getImageData(left, top, width, height);
  // const result_canvas = document.createElement('canvas');
  // result_canvas.width = image_data.width;
  // result_canvas.height = image_data.height;
  // result_canvas.getContext('2d').putImageData(image_data, 0, 0);
}
